/**
 * P1-A · Manual Bulk Attendance Upload · Admin surface.
 *
 * Route: /attendance/bulk-upload
 * Capabilities:
 *   attendance:bulk_upload:read       — view page + history + template
 *   attendance:bulk_upload:dry_run    — run the validation preview
 *   attendance:bulk_upload:commit     — commit a passing dry-run
 *   attendance:bulk_upload:overwrite  — supersede prior manual snapshots
 *   attendance:bulk_upload:rollback   — unwind a committed upload
 *
 * Flow (Admin authorised operational contract):
 *   Upload  →  Dry Run  →  Comparison table  →  Explicit Commit
 *           ↘  Upload History (rollback opens a governed unwind)
 *
 * Comparison table columns (per locked contract):
 *   Employee | Date | Uploaded | Existing | Existing Source
 *            | Proposed Action | Errors / Warnings
 *
 * Timings are NEVER mandatory for Present / Half. The dry-run never
 * warns about missing times — the backend preserves the day-level
 * declaration through the canonical pipeline without synthesising
 * punch events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    CloudArrowUp, CheckCircle, XCircle, Warning, Info,
    ArrowClockwise, DownloadSimple, ArrowsClockwise, FileCsv, Trash,
    ClockCounterClockwise,
} from "@phosphor-icons/react";

import { api, API_BASE, formatApiError, getToken } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";


// ─────────────────────────────────────────────────────────────────
// Constants + tiny helpers
// ─────────────────────────────────────────────────────────────────


const SEVERITY_STYLES = {
    block: "bg-rose-50 border-rose-200 text-rose-700",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-slate-50 border-slate-200 text-slate-700",
};

const STATUS_LIFECYCLE_STYLES = {
    dry_run_done: "bg-slate-100 text-slate-700 border border-slate-300",
    committed: "bg-emerald-50 text-emerald-700 border border-emerald-300",
    rolled_back: "bg-rose-50 text-rose-700 border border-rose-300",
};

const SOURCE_STYLES = {
    manual_bulk: "bg-indigo-50 text-indigo-700 border-indigo-200",
    manual: "bg-slate-50 text-slate-700 border-slate-300",
    biometric: "bg-emerald-50 text-emerald-700 border-emerald-300",
    mobile: "bg-cyan-50 text-cyan-700 border-cyan-300",
    desktop_agent: "bg-blue-50 text-blue-700 border-blue-300",
};

function severityBadge(sev) {
    if (!sev) return null;
    const label = sev === "block" ? "Block" : sev === "warn" ? "Warn" : "OK";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${SEVERITY_STYLES[sev] || SEVERITY_STYLES.info}`}
            data-testid={`row-severity-${sev}`}
        >
            {sev === "block"
                ? <XCircle className="h-3 w-3" />
                : sev === "warn"
                ? <Warning className="h-3 w-3" />
                : <CheckCircle className="h-3 w-3" />}
            {label}
        </span>
    );
}

function sourceBadge(src) {
    if (!src) return <span className="text-xs text-slate-400">—</span>;
    const cls = SOURCE_STYLES[src] || "bg-slate-50 text-slate-600 border-slate-200";
    return (
        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
            {src}
        </span>
    );
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}


// ─────────────────────────────────────────────────────────────────
// Sub-component · summary chip strip
// ─────────────────────────────────────────────────────────────────


function SummaryStrip({ summary }) {
    if (!summary) return null;
    const cells = [
        { label: "Rows", value: summary.row_count, tone: "slate" },
        { label: "Blocking", value: summary.block_count, tone: summary.block_count > 0 ? "rose" : "slate" },
        { label: "Warnings", value: summary.warning_count, tone: summary.warning_count > 0 ? "amber" : "slate" },
        { label: "Inserts", value: summary.insert_count, tone: "indigo" },
        { label: "Supersedes", value: summary.supersede_count, tone: summary.supersede_count > 0 ? "purple" : "slate" },
        { label: "Leave rejected", value: summary.leave_rejected, tone: summary.leave_rejected > 0 ? "rose" : "slate" },
    ];
    const toneMap = {
        slate: "bg-slate-50 text-slate-700 border-slate-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
        purple: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="dry-run-summary-strip">
            {cells.map((c) => (
                <div key={c.label}
                    className={`rounded-md border px-3 py-2 ${toneMap[c.tone] || toneMap.slate}`}
                    data-testid={`summary-${c.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</div>
                    <div className="text-lg font-semibold">{c.value ?? 0}</div>
                </div>
            ))}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Sub-component · dry-run comparison table
// ─────────────────────────────────────────────────────────────────


function ComparisonTable({ rows }) {
    if (!rows?.length) return null;
    return (
        <div className="overflow-hidden rounded-md border bg-white" data-testid="dry-run-comparison-table">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-3 py-2">Row</th>
                            <th className="px-3 py-2">Employee</th>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Uploaded</th>
                            <th className="px-3 py-2">Existing</th>
                            <th className="px-3 py-2">Existing source</th>
                            <th className="px-3 py-2">Proposed action</th>
                            <th className="px-3 py-2">Errors / Warnings</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {rows.map((r) => (
                            <tr key={`${r.row_no}-${r.employee_code}-${r.date}`}
                                className={
                                    r.severity === "block" ? "bg-rose-50/40"
                                    : r.severity === "warn" ? "bg-amber-50/40"
                                    : ""
                                }
                                data-testid={`dry-run-row-${r.row_no}`}>
                                <td className="px-3 py-2 align-top font-mono text-[11px] text-slate-500">
                                    #{r.row_no}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="font-medium text-slate-900">{r.employee_code || "—"}</div>
                                    <div className="text-xs text-slate-500">{r.employee_name || "—"}</div>
                                </td>
                                <td className="px-3 py-2 align-top font-mono">
                                    {r.date || <span className="text-rose-600">{r.raw_date || "—"}</span>}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="font-medium">{r.status || <span className="text-rose-600">{r.raw_status || "—"}</span>}</div>
                                    <div className="text-[11px] text-slate-500 font-mono">
                                        {r.in_time || r.out_time
                                            ? `${r.in_time || "—"} → ${r.out_time || "—"}`
                                            : "no timings"}
                                        {r.half_period ? ` · ${r.half_period}` : ""}
                                        {r.working_units != null && r.working_units !== "INVALID"
                                            ? ` · WU ${r.working_units}`
                                            : ""}
                                    </div>
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {r.existing_attendance_status
                                        ? <>
                                            <div className="font-medium">{r.existing_attendance_status}</div>
                                            <div className="text-[11px] text-slate-500 font-mono">
                                                {r.existing_working_units != null ? `WU ${r.existing_working_units}` : ""}
                                            </div>
                                          </>
                                        : <span className="text-xs text-slate-400">— none —</span>}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {sourceBadge(r.existing_source)}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="flex items-start gap-2">
                                        {severityBadge(r.severity)}
                                        <div className="text-xs text-slate-700">{r.action || "—"}</div>
                                    </div>
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {(r.errors?.length || r.warnings?.length) ? (
                                        <ul className="space-y-1 text-xs">
                                            {(r.errors || []).map((e, i) => (
                                                <li key={`e-${i}`} className="text-rose-700 flex gap-1">
                                                    <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
                                                    <span>{e}</span>
                                                </li>
                                            ))}
                                            {(r.warnings || []).map((w, i) => (
                                                <li key={`w-${i}`} className="text-amber-800 flex gap-1">
                                                    <Warning className="mt-0.5 h-3 w-3 shrink-0" />
                                                    <span>{w}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Sub-component · upload-history table
// ─────────────────────────────────────────────────────────────────


function HistoryTable({ items, onOpen, onRollback, canRollback }) {
    if (!items?.length) {
        return (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-slate-500"
                data-testid="history-empty">
                No bulk uploads yet.
            </div>
        );
    }
    return (
        <div className="overflow-hidden rounded-md border bg-white" data-testid="history-table">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                            <th className="px-3 py-2">Uploaded</th>
                            <th className="px-3 py-2">File</th>
                            <th className="px-3 py-2">By</th>
                            <th className="px-3 py-2">Rows</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Period</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                        {items.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50/60"
                                data-testid={`history-row-${u.id}`}>
                                <td className="px-3 py-2 font-mono text-xs text-slate-600">
                                    {fmtDateTime(u.uploaded_at)}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2 text-slate-800">
                                        <FileCsv className="h-4 w-4 text-slate-400" />
                                        <span className="max-w-[220px] truncate" title={u.filename}>
                                            {u.filename || "—"}
                                        </span>
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-400">{u.id}</div>
                                </td>
                                <td className="px-3 py-2 text-slate-700 text-xs">
                                    {u.uploaded_by_email || u.uploaded_by_id || "—"}
                                </td>
                                <td className="px-3 py-2 text-slate-800">
                                    <div>{u.row_count ?? 0}</div>
                                    <div className="text-[11px] text-slate-500">
                                        {u.status === "committed"
                                            ? `${u.committed_row_count ?? 0} committed · ${u.insert_count ?? 0} insert · ${u.supersede_count ?? 0} supersede · ${u.event_count ?? 0} event(s)`
                                            : ""}
                                    </div>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_LIFECYCLE_STYLES[u.status] || "bg-slate-100 text-slate-700"}`}
                                        data-testid={`history-status-${u.id}`}>
                                        {u.status}
                                    </span>
                                    {u.overwrite_mode ? (
                                        <span className="ml-1 rounded-md bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                                            overwrite
                                        </span>
                                    ) : null}
                                </td>
                                <td className="px-3 py-2 text-xs font-mono text-slate-500">
                                    {u.period_span?.min_date || "—"} → {u.period_span?.max_date || "—"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex items-center gap-2">
                                        <Button variant="outline" size="sm"
                                            onClick={() => onOpen(u.id)}
                                            data-testid={`history-open-${u.id}`}>
                                            Open
                                        </Button>
                                        {u.status === "committed" && canRollback ? (
                                            <Button variant="destructive" size="sm"
                                                onClick={() => onRollback(u)}
                                                data-testid={`history-rollback-${u.id}`}>
                                                <ClockCounterClockwise className="mr-1 h-3 w-3" />
                                                Rollback
                                            </Button>
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────


export default function AttendanceBulkUpload() {
    const { can } = useAuth();
    const isRead = can?.("attendance:bulk_upload:read");
    const canDryRun = can?.("attendance:bulk_upload:dry_run");
    const canCommit = can?.("attendance:bulk_upload:commit");
    const canOverwrite = can?.("attendance:bulk_upload:overwrite");
    const canRollback = can?.("attendance:bulk_upload:rollback");

    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [overwriteMode, setOverwriteMode] = useState(false);
    const [dryRunLoading, setDryRunLoading] = useState(false);
    const [dryRun, setDryRun] = useState(null);   // { upload, report }
    const [committing, setCommitting] = useState(false);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Detail / rollback dialogs
    const [detail, setDetail] = useState(null);
    const [rollbackTarget, setRollbackTarget] = useState(null);
    const [rollbackReason, setRollbackReason] = useState("");
    const [rollingBack, setRollingBack] = useState(false);

    // ── History fetch ──
    const refreshHistory = useCallback(async () => {
        if (!isRead) return;
        setHistoryLoading(true);
        try {
            const r = await api.get("/attendance/bulk-upload", { params: { limit: 100 } });
            setHistory(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load history");
        } finally {
            setHistoryLoading(false);
        }
    }, [isRead]);

    useEffect(() => { refreshHistory(); }, [refreshHistory]);

    // ── Template download ──
    const downloadTemplate = async () => {
        try {
            // Use fetch directly to keep the blob-download semantics simple.
            const resp = await fetch(`${API_BASE}/attendance/bulk-upload/template`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "attendance_bulk_upload_template.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(`Template download failed: ${err.message || err}`);
        }
    };

    // ── Dry-run ──
    const runDryRun = async () => {
        if (!file) {
            toast.warning("Choose a .csv or .xlsx file first.");
            return;
        }
        setDryRunLoading(true);
        setDryRun(null);
        try {
            const fd = new FormData();
            fd.append("file", file, file.name);
            fd.append("overwrite_mode", overwriteMode ? "true" : "false");
            const r = await api.post("/attendance/bulk-upload/dry-run", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDryRun(r.data);
            const s = r.data?.report?.summary || {};
            if (s.block_count > 0) {
                toast.error(`Dry-run has ${s.block_count} blocking error(s). Fix and re-upload.`);
            } else if (s.warning_count > 0) {
                toast.warning(`Dry-run OK — ${s.warning_count} warning(s) surfaced. Review before commit.`);
            } else {
                toast.success(`Dry-run OK — ${s.insert_count || 0} insert(s) · ${s.supersede_count || 0} supersede(s).`);
            }
            refreshHistory();
        } catch (err) {
            toast.error(formatApiError(err) || "Dry-run failed");
        } finally {
            setDryRunLoading(false);
        }
    };

    // ── Commit ──
    const runCommit = async () => {
        if (!dryRun?.upload?.id) return;
        const s = dryRun.report?.summary || {};
        if (s.block_count > 0) {
            toast.error("Cannot commit — resolve blocking errors first.");
            return;
        }
        const supersedes = s.supersede_count || 0;
        if (supersedes > 0 && !canOverwrite) {
            toast.error("This commit will supersede existing manual snapshots — you need `attendance:bulk_upload:overwrite`.");
            return;
        }
        const confirmMsg = supersedes > 0
            ? `Commit ${s.insert_count} insert(s) and supersede ${supersedes} existing manual snapshot(s)?`
            : `Commit ${s.insert_count} manual_bulk snapshot(s)?`;
        if (!window.confirm(confirmMsg)) return;

        setCommitting(true);
        try {
            const r = await api.post(`/attendance/bulk-upload/${dryRun.upload.id}/commit`);
            const d = r.data || {};
            const parts = [
                `${d.committed_row_count ?? 0} row(s) committed`,
                `${d.insert_count ?? 0} insert`,
                `${d.supersede_count ?? 0} supersede`,
                `${d.event_count ?? 0} event(s)`,
            ];
            toast.success(
                `Committed — ${parts.join(" · ")} · AAS ${d.aas_action_id || "?"}.`,
            );
            setDryRun(null);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setOverwriteMode(false);
            refreshHistory();
        } catch (err) {
            toast.error(formatApiError(err) || "Commit failed");
        } finally {
            setCommitting(false);
        }
    };

    // ── History detail ──
    const openDetail = async (uploadId) => {
        try {
            const r = await api.get(`/attendance/bulk-upload/${uploadId}`);
            setDetail(r.data);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load upload detail");
        }
    };

    // ── Rollback ──
    const openRollback = (upload) => {
        setRollbackTarget(upload);
        setRollbackReason("");
    };

    const submitRollback = async () => {
        if (!rollbackTarget?.id) return;
        if ((rollbackReason || "").trim().length < 10) {
            toast.warning("Rollback reason must be at least 10 characters.");
            return;
        }
        setRollingBack(true);
        try {
            const r = await api.post(
                `/attendance/bulk-upload/${rollbackTarget.id}/rollback`,
                { reason: rollbackReason.trim() },
            );
            toast.success(
                `Rolled back — restored ${r.data?.snapshots_restored ?? 0} · deleted ${r.data?.snapshots_deleted ?? 0} · events ${r.data?.events_rolled_back ?? 0}.`,
            );
            setRollbackTarget(null);
            setRollbackReason("");
            refreshHistory();
        } catch (err) {
            toast.error(formatApiError(err) || "Rollback failed");
        } finally {
            setRollingBack(false);
        }
    };

    // ── Auth guard ──
    if (!isRead) {
        return (
            <EmptyState
                title="Not authorised"
                description="Manual Bulk Attendance Upload requires the `attendance:bulk_upload:read` capability."
                data-testid="bulk-upload-forbidden"
            />
        );
    }

    const summary = dryRun?.report?.summary || null;
    const canPressCommit = !!dryRun?.upload?.id
        && summary?.block_count === 0
        && !committing
        && canCommit;

    return (
        <div className="space-y-6 pb-10" data-testid="bulk-upload-page">
            <PageHeader
                eyebrow="ATTENDANCE · ADMIN"
                title="Manual Bulk Attendance Upload"
                description="Upload attendance declarations (CSV / XLSX) as a `manual_bulk` source. Dry-run first · then commit explicitly. Never overwrites biometric / mobile / desktop-agent data. Timings are optional for Present / Half — no punch events are ever manufactured."
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={downloadTemplate}
                            data-testid="btn-download-template">
                            <DownloadSimple className="mr-1 h-4 w-4" />
                            Download template
                        </Button>
                        <Button variant="outline" size="sm" onClick={refreshHistory}
                            disabled={historyLoading}
                            data-testid="btn-refresh-history">
                            <ArrowsClockwise className="mr-1 h-4 w-4" />
                            Refresh
                        </Button>
                    </div>
                }
            />

            <div className="space-y-6 px-6 sm:px-8">

                {/* ── Upload panel ─────────────────────────────────── */}
                <section className="rounded-lg border bg-white p-5 shadow-sm" data-testid="upload-panel">
                    <div className="mb-3 flex items-center gap-2">
                        <CloudArrowUp className="h-5 w-5 text-indigo-600" />
                        <h2 className="text-base font-semibold text-slate-900">1 · Upload &amp; dry-run</h2>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="lg:col-span-2">
                            <Label htmlFor="bulk-file">CSV or XLSX file</Label>
                            <Input
                                ref={fileInputRef}
                                id="bulk-file"
                                type="file"
                                accept=".csv,.txt,.xlsx,.xlsm"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="input-bulk-file"
                                disabled={!canDryRun || dryRunLoading}
                            />
                            <div className="mt-1 text-xs text-slate-500">
                                Required columns:{" "}
                                <code className="rounded bg-slate-100 px-1">employee_code</code>{" "}
                                <code className="rounded bg-slate-100 px-1">date</code>{" "}
                                <code className="rounded bg-slate-100 px-1">status</code>.
                                Optional:{" "}
                                <code className="rounded bg-slate-100 px-1">in_time</code>{" "}
                                <code className="rounded bg-slate-100 px-1">out_time</code>{" "}
                                <code className="rounded bg-slate-100 px-1">half_period</code>{" "}
                                <code className="rounded bg-slate-100 px-1">working_units</code>{" "}
                                <code className="rounded bg-slate-100 px-1">remarks</code>.
                                Statuses: Present · Absent · Half · Weekly-Off · Holiday. Leave rows are rejected.
                                Max 50 000 rows / 25 MB.
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 ${canOverwrite ? "" : "cursor-not-allowed opacity-60"}`}>
                                    <input
                                        type="checkbox"
                                        checked={overwriteMode}
                                        onChange={(e) => setOverwriteMode(e.target.checked)}
                                        disabled={!canOverwrite || dryRunLoading}
                                        data-testid="input-overwrite-mode"
                                        className="mt-1"
                                    />
                                    <div className="text-xs text-slate-700">
                                        <div className="font-medium">Overwrite existing manual snapshots</div>
                                        <div className="text-slate-500">
                                            Required to supersede prior <code>manual</code> or{" "}
                                            <code>manual_bulk</code> rows. Never overrides biometric / mobile / desktop-agent.
                                            {!canOverwrite ? (
                                                <span className="mt-1 block text-rose-600">
                                                    You lack <code>attendance:bulk_upload:overwrite</code>.
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </label>
                            </div>
                            <Button
                                onClick={runDryRun}
                                disabled={!file || !canDryRun || dryRunLoading}
                                className="w-full"
                                data-testid="btn-run-dry-run"
                            >
                                {dryRunLoading ? (
                                    <><ArrowClockwise className="mr-1 h-4 w-4 animate-spin" /> Running dry-run…</>
                                ) : (
                                    <>Run dry-run</>
                                )}
                            </Button>
                        </div>
                    </div>
                </section>

                {/* ── Dry-run report ───────────────────────────────── */}
                {dryRun ? (
                    <section className="rounded-lg border bg-white p-5 shadow-sm" data-testid="dry-run-panel">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <Info className="h-5 w-5 text-indigo-600" />
                                <h2 className="text-base font-semibold text-slate-900">
                                    2 · Dry-run comparison
                                </h2>
                                <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600">
                                    {dryRun.upload.id}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm"
                                    onClick={() => { setDryRun(null); setFile(null); if (fileInputRef.current) fileInputRef.current.value=""; }}
                                    data-testid="btn-discard-dry-run">
                                    <Trash className="mr-1 h-4 w-4" />
                                    Discard
                                </Button>
                                <Button
                                    onClick={runCommit}
                                    disabled={!canPressCommit}
                                    data-testid="btn-commit"
                                >
                                    {committing ? (
                                        <><ArrowClockwise className="mr-1 h-4 w-4 animate-spin" /> Committing…</>
                                    ) : (
                                        <>3 · Commit {summary?.insert_count ?? 0} + {summary?.supersede_count ?? 0} rows</>
                                    )}
                                </Button>
                            </div>
                        </div>

                        <SummaryStrip summary={summary} />

                        {summary?.block_count > 0 ? (
                            <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
                                data-testid="dry-run-block-banner">
                                <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    <div className="font-semibold">Blocked — commit is not allowed.</div>
                                    <div className="text-xs">
                                        {summary.block_count} row(s) failed validation. Fix them in the file and re-upload.
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {summary?.warning_count > 0 && summary?.block_count === 0 ? (
                            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                                data-testid="dry-run-warn-banner">
                                <Warning className="mt-0.5 h-4 w-4 shrink-0" />
                                <div>
                                    <div className="font-semibold">Warnings surfaced.</div>
                                    <div className="text-xs">
                                        {summary.warning_count} row(s) either supersede existing manual data
                                        or clash with the calendar. Review before committing.
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-4">
                            <ComparisonTable rows={dryRun.report?.rows || []} />
                        </div>
                    </section>
                ) : null}

                {/* ── History ─────────────────────────────────────── */}
                <section className="rounded-lg border bg-white p-5 shadow-sm" data-testid="history-panel">
                    <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ClockCounterClockwise className="h-5 w-5 text-slate-500" />
                            <h2 className="text-base font-semibold text-slate-900">Upload history</h2>
                        </div>
                        <div className="text-xs text-slate-500">{history.length} entries</div>
                    </div>
                    <HistoryTable
                        items={history}
                        onOpen={openDetail}
                        onRollback={openRollback}
                        canRollback={canRollback}
                    />
                </section>
            </div>

            {/* ── Detail dialog ───────────────────────────────── */}
            <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
                <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto" data-testid="upload-detail-dialog">
                    {detail ? (
                        <>
                            <DialogHeader>
                                <DialogTitle>Upload · {detail.id}</DialogTitle>
                                <DialogDescription>
                                    {detail.filename} · uploaded {fmtDateTime(detail.uploaded_at)}
                                    {" · "}
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_LIFECYCLE_STYLES[detail.status] || ""}`}>
                                        {detail.status}
                                    </span>
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <SummaryStrip summary={detail.summary_counts || detail.dry_run_report?.summary} />
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Uploader</div>
                                        <div className="font-medium">{detail.uploaded_by_email || detail.uploaded_by_id || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Overwrite</div>
                                        <div className="font-medium">{detail.overwrite_mode ? "Yes" : "No"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Period</div>
                                        <div className="font-mono">
                                            {detail.period_span?.min_date || "—"} → {detail.period_span?.max_date || "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">AAS action</div>
                                        <div className="font-mono">{detail.aas_action_id || "—"}</div>
                                    </div>
                                    {detail.status === "committed" ? (
                                        <>
                                            <div>
                                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Committed at</div>
                                                <div>{fmtDateTime(detail.committed_at)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Committed by</div>
                                                <div>{detail.committed_by_email || "—"}</div>
                                            </div>
                                        </>
                                    ) : null}
                                    {detail.status === "rolled_back" ? (
                                        <>
                                            <div>
                                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Rolled back at</div>
                                                <div>{fmtDateTime(detail.rolled_back_at)}</div>
                                            </div>
                                            <div>
                                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Rolled back by</div>
                                                <div>{detail.rolled_back_by_email || "—"}</div>
                                            </div>
                                            <div className="col-span-2">
                                                <div className="text-[11px] uppercase tracking-wide text-slate-500">Reason</div>
                                                <div className="text-sm">{detail.rolled_back_reason || "—"}</div>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                                {detail.dry_run_report?.rows?.length ? (
                                    <div>
                                        <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">
                                            Dry-run detail ({detail.dry_run_report.rows.length} rows)
                                        </div>
                                        <ComparisonTable rows={detail.dry_run_report.rows} />
                                    </div>
                                ) : null}
                            </div>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* ── Rollback dialog ────────────────────────────── */}
            <Dialog open={!!rollbackTarget} onOpenChange={(o) => { if (!o && !rollingBack) { setRollbackTarget(null); setRollbackReason(""); } }}>
                <DialogContent className="max-w-lg" data-testid="rollback-dialog">
                    {rollbackTarget ? (
                        <>
                            <DialogHeader>
                                <DialogTitle>Rollback · {rollbackTarget.id}</DialogTitle>
                                <DialogDescription>
                                    Restores the prior <code>manual</code> snapshots this upload superseded,
                                    removes rows this upload freshly inserted, and flips linked events to
                                    <code> rolled_back</code>. Blocked if any affected day is inside a locked payroll run.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">File</div>
                                        <div className="font-medium">{rollbackTarget.filename || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Committed rows</div>
                                        <div className="font-medium">{rollbackTarget.committed_row_count || 0}</div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Period</div>
                                        <div className="font-mono">
                                            {rollbackTarget.period_span?.min_date || "—"} →{" "}
                                            {rollbackTarget.period_span?.max_date || "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Overwrite</div>
                                        <div>{rollbackTarget.overwrite_mode ? "Yes" : "No"}</div>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="rollback-reason">Reason (min 10 chars)</Label>
                                    <Textarea
                                        id="rollback-reason"
                                        rows={3}
                                        value={rollbackReason}
                                        onChange={(e) => setRollbackReason(e.target.value)}
                                        placeholder="Explain why this bulk upload is being rolled back…"
                                        data-testid="input-rollback-reason"
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline"
                                    onClick={() => { setRollbackTarget(null); setRollbackReason(""); }}
                                    disabled={rollingBack}
                                    data-testid="btn-rollback-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={submitRollback}
                                    disabled={rollingBack || rollbackReason.trim().length < 10}
                                    data-testid="btn-rollback-confirm">
                                    {rollingBack ? (
                                        <><ArrowClockwise className="mr-1 h-4 w-4 animate-spin" /> Rolling back…</>
                                    ) : (
                                        <>Rollback upload</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
